import React, { useEffect, useMemo, useState } from 'react';
import { MessageSquare, ShoppingBag, FileText, Bell, Phone, Mail, AlertCircle, Sparkles, UserCheck } from 'lucide-react';

/**
 * CustomerTimeline: cronologia unificata delle attività su un cliente.
 * Aggrega: note CRM, ordini, preventivi, azioni funnel (contacted/lost),
 * assegnazione agente, follow-up pianificati.
 */
const API = process.env.REACT_APP_BACKEND_URL;

const ICON = {
  note: MessageSquare,
  order: ShoppingBag,
  quote: FileText,
  action: Phone,
  follow_up: Bell,
  agent: UserCheck,
  system: Sparkles,
};

const COLOR = {
  note: 'bg-purple-100 text-purple-700 border-purple-200',
  order: 'bg-blue-100 text-blue-700 border-blue-200',
  quote: 'bg-amber-100 text-amber-700 border-amber-200',
  action: 'bg-teal-100 text-teal-700 border-teal-200',
  follow_up: 'bg-pink-100 text-pink-700 border-pink-200',
  agent: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
  system: 'bg-gray-100 text-gray-700 border-gray-200',
};

const CustomerTimeline = ({ customerId, adminToken, customerData, notes }) => {
  const [funnelActions, setFunnelActions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/crm/funnel/actions/user/${customerId}`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        });
        if (r.ok) {
          const j = await r.json();
          setFunnelActions(j.items || j.actions || []);
        }
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [customerId, adminToken]);

  const events = useMemo(() => {
    const list = [];
    // Note CRM
    (notes || []).forEach((n) => {
      list.push({
        type: 'note',
        id: `note-${n.id || n.created_at}`,
        title: n.content?.slice(0, 100) || 'Nota',
        detail: n.content?.length > 100 ? n.content : null,
        actor: n.created_by,
        at: n.created_at,
      });
      if (n.follow_up_at) {
        list.push({
          type: 'follow_up',
          id: `fu-${n.id || n.follow_up_at}`,
          title: `Follow-up pianificato — ${n.follow_up_channel || 'canale non specificato'}`,
          actor: n.created_by,
          at: n.follow_up_at,
        });
      }
    });
    // Orders
    (customerData?.orders || []).forEach((o) => {
      list.push({
        type: 'order',
        id: `order-${o.id || o.orderNumber}`,
        title: `Ordine ${o.orderNumber || o.id} — ${o.status}`,
        detail: `Totale: € ${(o.totalAmount || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`,
        at: o.createdAt,
      });
    });
    // Quotes
    (customerData?.quotes || []).forEach((q) => {
      list.push({
        type: 'quote',
        id: `quote-${q.id || q.quote_number}`,
        title: `Preventivo ${q.quote_number || q.id} — ${q.status}`,
        detail: `Totale: € ${(q.total || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`,
        at: q.created_at,
      });
    });
    // Funnel actions
    (funnelActions || []).forEach((a) => {
      const isLost = a.action === 'lost';
      list.push({
        type: 'action',
        id: `action-${a.id || a.created_at}`,
        title: isLost
          ? `Cliente marcato come perso — ${a.lost_reason || 'motivo non specificato'}`
          : `Contattato via ${a.channel || 'canale non specificato'}`,
        detail: a.note,
        actor: a.admin_email,
        at: a.created_at,
        icon: isLost ? AlertCircle : (a.channel === 'email' ? Mail : Phone),
      });
    });
    // Agent assignment
    if (customerData?.customer?.assignedAt) {
      list.push({
        type: 'agent',
        id: 'agent-assign',
        title: `Assegnato all'agente ${customerData.customer.assignedAgentName || customerData.customer.assignedAgentEmail}`,
        actor: customerData.customer.assignedBy,
        at: customerData.customer.assignedAt,
      });
    }
    // Registration
    if (customerData?.customer?.createdAt) {
      list.push({
        type: 'system',
        id: 'signup',
        title: 'Cliente registrato in piattaforma',
        detail: customerData.customer.leadSource ? `Sorgente: ${customerData.customer.leadSource}` : null,
        at: customerData.customer.createdAt,
      });
    }
    return list.filter((e) => e.at).sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [notes, customerData, funnelActions]);

  const fmt = (d) => {
    try {
      return new Date(d).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return String(d); }
  };

  if (loading) {
    return <div className="py-16 text-center text-gray-500">Caricamento timeline…</div>;
  }
  if (events.length === 0) {
    return (
      <div className="py-16 text-center">
        <Sparkles className="w-8 h-8 mx-auto text-purple-300 mb-2" />
        <p className="text-gray-500 text-sm">Nessuna attività registrata per questo cliente.</p>
      </div>
    );
  }

  return (
    <div className="relative pl-6" data-testid="customer-timeline">
      {/* vertical line */}
      <div className="absolute left-3 top-0 bottom-0 w-px bg-gradient-to-b from-purple-200 via-gray-200 to-transparent" />
      <div className="space-y-4">
        {events.map((ev) => {
          const Icon = ev.icon || ICON[ev.type] || Sparkles;
          const color = COLOR[ev.type] || COLOR.system;
          return (
            <div key={ev.id} className="relative" data-testid={`timeline-event-${ev.type}`}>
              <div className={`absolute -left-6 top-1 w-6 h-6 rounded-full flex items-center justify-center border ${color}`}>
                <Icon className="w-3 h-3" />
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-3 hover:border-purple-300 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900">{ev.title}</div>
                    {ev.detail && <div className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{ev.detail}</div>}
                  </div>
                  <div className="text-[11px] text-gray-500 shrink-0 tabular-nums">{fmt(ev.at)}</div>
                </div>
                {ev.actor && (
                  <div className="text-[10px] text-gray-500 mt-2">di <span className="font-medium">{ev.actor}</span></div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomerTimeline;
